import { Feature, GeoJSON, Point } from 'geojson'
import WoosmapMultisearch from './MultisearchTypes'

export type WoosmapEvent =
  | 'center_changed'
  | 'store_selected'
  | 'store_unselected'
  | 'zoom_changed'
  | 'bounds_changed'
  | 'idle'

export interface WoosmapGeocodingGeometry {
  location_type: 'ROOFTOP' | 'RANGE_INTERPOLATED' | 'GEOMETRIC_CENTER' | 'APPROXIMATE'
  location: {
    lat: number
    lng: number
  }
}

export type WoosmapGeocodingResultType =
  | 'place'
  | 'shopping'
  | 'business_and_services'
  | 'house_number'
  | 'route'
  | 'locality'
  | 'postal_code'
  | 'country'
  | 'eat_and_drink'
  | 'going_out_entertainment'
  | 'sights_and_museums'
  | 'natural_and_geographical'
  | 'transport'
  | 'accommodations'
  | 'leisure_and_outdoor'
  | 'facilities'
  | 'areas_and_buildings'

export type WoosmapAddressComponentType =
  | 'locality'
  | 'country'
  | 'postal_code'
  | 'state'
  | 'county'
  | 'district'
  | 'route'
  | 'street_number'

export interface WoosmapLoaderOptions {
  version: string
  publicKey: string
  callback?: () => void
  loadJquery: boolean
}

export interface WoosmapSearchParams {
  lat: number
  lng: number
  query: string
  storesByPage: number
  maxDistanceFromUser: number
}

export interface SearchParameters {
  new (params: Partial<WoosmapSearchParams>): any
}

export interface CustomPropertiesAddress {
  lines: Array<string> | null
  zipcode: string
  country_code: string
  city: string
}

export type CustomPropertiesUserProperties = Record<string, any> & {
  pictures?: Array<string>
  tenantId?: string
  wasteBoxId?: string
  externalId?: string
  description?: string
  province?: string
  brandProgramAffiliation?: string
}

export interface FeatureContact {
  email?: string
  phone?: string
  website?: string
}

export interface WoosmapDaySchedule {
  start: string
  end: string
}

export interface WoosmapSchedule {
  usual: Record<number, Array<WoosmapDaySchedule>>
}

export interface WoosmapOpen {
  open_now: boolean
}

export interface CustomProperties {
  description?: string
  tags: Array<string>
  types: Array<string>
  user_properties: CustomPropertiesUserProperties | null
  store_id: string
  name: string
  distance?: number
  contact: FeatureContact | null
  address?: CustomPropertiesAddress
  opening_hours?: WoosmapSchedule
  open?: WoosmapOpen
}

export type WoosmapFeature = Feature<Point, CustomProperties>

export interface PredictionMatchedSubstring {
  offset: number
  length: number
}
export interface WoosmapPrediction {
  store_id: string
  name: string
  types: string[]
  matched_substrings: PredictionMatchedSubstring[]
  highlighted: string
}

export interface WoosmapAutocompleteSearchResponse {
  predictions: WoosmapPrediction[]
}

export interface SearchResult extends GeoJSON.FeatureCollection<Point, CustomProperties> {
  pagination: {
    page: number
    pageCount: number
  }
}

export interface WoosmapDataSource {
  searchStoresByParameters: (params: SearchParameters, callback: (data: SearchResult) => void) => void
}

export interface WoosmapPoint {
  x: number
  y: number
}

export type WoosmapLatLng = {
  lat: () => number
  lng: () => number
}

export type WoosmapLatLngLiteral = {
  lat: number
  lng: number
}

export type WoosmapPanPadding = {
  top?: number
  bottom?: number
  left?: number
  right?: number
}

export interface MapProps {
  center: {
    lat: number
    lng: number
  }
  zoom: number
  disableTilt: boolean
  disableDefaultUI: boolean
  styles?: Array<any>
}

export interface FlyToParams {
  center: WoosmapLatLngLiteral
  zoom: number
}

export interface WoosmapMap {
  getCenter: () => WoosmapLatLng
  setCenter: (latLng: WoosmapLatLng | WoosmapLatLngLiteral, padding?: WoosmapPanPadding) => void
  setZoom: (zoom: number) => void
  getZoom: () => number
  panTo: (latLng: WoosmapLatLng | WoosmapLatLngLiteral, padding?: WoosmapPanPadding) => void
  flyTo: (params: FlyToParams) => void
}

export interface WoosmapStoreOverlay {
  setMap: (map: WoosmapMap) => void
  setQuery: (query: string) => void
  setSelection: (feature: WoosmapFeature) => void
  clearSelection: () => void
}

export interface WoosmapTiledView {
  set: (name: 'location' | 'stores', data: any) => void
}

export interface WoosmapOverlayIcon {
  url: string
  size?: any
  scaledSize?: any
  anchor?: WoosmapPoint
}

export interface WoosmapOverlayRule {
  color: string
  type: string
  icon: WoosmapOverlayIcon
  selectedIcon?: WoosmapOverlayIcon
}

export interface WoosmapStoreOverlayParams {
  breakPoint: number
  default: {
    color: string
    minSize?: number
    size?: number
    icon: WoosmapOverlayIcon
    selectedIcon?: WoosmapOverlayIcon
  }
  rules: Array<WoosmapOverlayRule>
}

export interface WoosmapMarkerParams {
  position: WoosmapLatLng | WoosmapLatLngLiteral
  clickable?: boolean
  opacity?: number
  visible?: boolean
  draggable?: boolean
  icon?: WoosmapOverlayIcon
  map?: WoosmapMap
  anchorPoint?: WoosmapPoint
}

export interface WoosmapMarker {
  setMap: (map: WoosmapMap | null) => void
  addListener: (event: string, callback: () => void) => WoosmapMapEventListener
  getPosition: () => WoosmapLatLng
  setIcon: (icon: WoosmapOverlayIcon | string) => void
  setDraggable: (shouldBeDraggable: boolean) => WoosmapMarker
  setPosition: (position: WoosmapLatLngLiteral) => void
}

export interface WoosmapMapEventListener {
  remove: () => void
}

export interface WoosmapGeocodingGeometry {
  location_type: 'ROOFTOP' | 'RANGE_INTERPOLATED' | 'GEOMETRIC_CENTER' | 'APPROXIMATE'
  location: {
    lat: number
    lng: number
  }
}

export interface WoosmapGeocodingAddressComponent {
  long_name: string
  short_name: string
  types: Array<WoosmapAddressComponentType>
}

export interface WoosmapGeocodingResult {
  address_components: Array<WoosmapGeocodingAddressComponent>
  formatted_address: string
  geometry: WoosmapGeocodingGeometry
  distance?: number
  types: Array<WoosmapGeocodingResultType>
}

export interface WoosmapGeocodingData {
  results: Array<WoosmapGeocodingResult>
  status: 'OK' | 'INVALID_REQUEST' | 'REQUEST_DENIED' | 'UNKNOWN_ERROR'
}

declare global {
  interface Window {
    WoosmapLoader: {
      load: (options: WoosmapLoaderOptions) => void
    }
    woosmap: {
      DataSource: {
        new (): WoosmapDataSource
      }
      TiledView: {
        new (map: WoosmapMap, options?: any): WoosmapTiledView
      }
      search: {
        SearchParameters: SearchParameters
      }
      query: {
        or: (params: any) => string
        and: (params: any) => string
        F: (field: string, value: string) => void
      }
      map: {
        Map: {
          new (element: any, props?: Partial<MapProps>): WoosmapMap
        }
        StoresOverlay: {
          new (params: WoosmapStoreOverlayParams): WoosmapStoreOverlay
        }
        Marker: {
          new (params: WoosmapMarkerParams): WoosmapMarker
        }
        event: {
          addListener: (
            map: WoosmapMap,
            event: WoosmapEvent,
            callback: (store: WoosmapFeature) => void
          ) => WoosmapMapEventListener
          addListenerOnce: (
            map: WoosmapMap,
            event: WoosmapEvent,
            callback: (store: WoosmapFeature) => void
          ) => WoosmapMapEventListener
          removeListener: (callback: WoosmapMapEventListener) => void
        }
      }
      multisearch(config: WoosmapMultisearch.Config): WoosmapMultisearch.MultiSearchObject
    }
    woosmapMapCallback?: () => void
  }
}

const loadWoosmapSdk = (scriptId: string): Promise<void> => {
  return new Promise((resolve) => {
    if (document.getElementById(scriptId) === null) {
      const script = document.createElement('script')
      script.setAttribute('id', scriptId)
      script.defer = true
      script.src = 'https://sdk.woosmap.com/locator/loader.js'
      script.onload = () => {
        resolve()
      }
      document.head.append(script)
    } else {
      resolve()
    }
  })
}

const loadWoosmapMap = (scriptId: string, publicKey: string, callback?: string): Promise<void> => {
  return new Promise((resolve) => {
    if (document.getElementById(scriptId) === null) {
      const script = document.createElement('script')
      script.setAttribute('id', scriptId)
      script.defer = true
      script.src = `https://sdk.woosmap.com/map/map.js?key=${publicKey}&callback=${callback}`
      script.onload = () => {
        resolve()
      }
      document.head.append(script)
    } else {
      resolve()
    }
  })
}

const loadWoosmapMultisearch = (scriptId: string): Promise<void> => {
  return new Promise((resolve) => {
    if (document.getElementById(scriptId) === null) {
      const script = document.createElement('script')
      script.setAttribute('id', scriptId)
      script.defer = true
      script.src = `https://sdk.woosmap.com/multisearch/multisearch.js`
      script.onload = () => {
        resolve()
      }
      document.head.append(script)
    } else {
      resolve()
    }
  })
}

export { loadWoosmapSdk, loadWoosmapMap, loadWoosmapMultisearch }
